import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import api from '../../services/api';
import { ApiResponse } from '../../types';

export interface Notification {
  id: string;
  type: 'payment' | 'subscription' | 'system' | 'security';
  title: string;
  message: string;
  severity: 'info' | 'success' | 'warning' | 'error';
  isRead: boolean;
  createdAt: string;
  actionUrl?: string;
  metadata?: Record<string, any>;
}

export interface NotificationPreferences {
  emailEnabled: boolean;
  pushEnabled: boolean;
  paymentNotifications: boolean;
  subscriptionNotifications: boolean;
  systemNotifications: boolean;
  securityNotifications: boolean;
}

interface NotificationsState {
  notifications: Notification[];
  unreadCount: number;
  preferences: NotificationPreferences | null;
  loading: boolean;
  error: string | null;
}

const initialState: NotificationsState = {
  notifications: [],
  unreadCount: 0,
  preferences: null,
  loading: false,
  error: null,
};

export const fetchNotifications = createAsyncThunk(
  'notifications/fetchNotifications',
  async ({ page = 1, pageSize = 20 }: { page?: number; pageSize?: number } = {}) => {
    try {
      const response = await api.get<ApiResponse<Notification[]>>(`/notifications?page=${page}&pageSize=${pageSize}`);
      return response.data.data;
    } catch (error: any) {
      // Fallback to mock data for development
      const mockNotifications: Notification[] = [
        {
          id: '1',
          type: 'payment',
          title: '決済完了',
          message: '顧客 cust_001 の決済 ¥1,500 が完了しました。',
          severity: 'success',
          isRead: false,
          createdAt: new Date(Date.now() - 5 * 60 * 1000).toISOString(), // 5 minutes ago
          actionUrl: '/payments/pay_001',
          metadata: { customerId: 'cust_001', amount: 1500 }
        },
        {
          id: '2',
          type: 'subscription',
          title: 'サブスクリプション更新',
          message: '顧客 cust_002 のサブスクリプションが更新されました。',
          severity: 'info',
          isRead: false,
          createdAt: new Date(Date.now() - 15 * 60 * 1000).toISOString(), // 15 minutes ago
          actionUrl: '/subscriptions/sub_002',
          metadata: { customerId: 'cust_002', subscriptionId: 'sub_002' }
        },
        {
          id: '3',
          type: 'payment',
          title: '決済失敗',
          message: '顧客 cust_003 の決済 ¥3,000 が失敗しました。カード期限切れの可能性があります。',
          severity: 'error',
          isRead: true,
          createdAt: new Date(Date.now() - 60 * 60 * 1000).toISOString(), // 1 hour ago
          actionUrl: '/payments/pay_003',
          metadata: { customerId: 'cust_003', amount: 3000, errorCode: 'card_expired' }
        },
        {
          id: '4',
          type: 'system',
          title: 'システムメンテナンス',
          message: '明日 2:00-4:00 にシステムメンテナンスを実施します。',
          severity: 'warning',
          isRead: true,
          createdAt: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(), // 2 hours ago
          metadata: { maintenanceStart: '2025-06-13T02:00:00Z', maintenanceEnd: '2025-06-13T04:00:00Z' }
        },
        {
          id: '5',
          type: 'security',
          title: '新規APIキー作成',
          message: '新しいAPIキー "Test API Key" が作成されました。',
          severity: 'info',
          isRead: true,
          createdAt: new Date(Date.now() - 3 * 60 * 60 * 1000).toISOString(), // 3 hours ago
          actionUrl: '/settings',
          metadata: { apiKeyName: 'Test API Key' }
        }
      ];
      
      return mockNotifications;
    }
  }
);

export const markAsRead = createAsyncThunk(
  'notifications/markAsRead',
  async (notificationId: string) => {
    try {
      await api.patch(`/notifications/${notificationId}/read`);
      return notificationId;
    } catch (error: any) {
      // For mock, just return the ID
      return notificationId;
    }
  }
);

export const markAllAsRead = createAsyncThunk(
  'notifications/markAllAsRead',
  async () => {
    try {
      await api.patch('/notifications/read-all');
      return true;
    } catch (error: any) {
      // For mock, just return true
      return true;
    }
  }
);

export const sendNotification = createAsyncThunk(
  'notifications/sendNotification',
  async (notificationData: { 
    customerId?: string; 
    type: string; 
    title: string; 
    message: string;
    severity?: 'info' | 'success' | 'warning' | 'error';
  }) => {
    try {
      const response = await api.post<ApiResponse<Notification>>('/notifications/send', notificationData);
      return response.data.data;
    } catch (error: any) {
      // Fallback to mock data
      return {
        id: `notif_${Date.now()}`,
        type: notificationData.type as any,
        title: notificationData.title,
        message: notificationData.message,
        severity: notificationData.severity || 'info',
        isRead: false,
        createdAt: new Date().toISOString(),
        metadata: { customerId: notificationData.customerId }
      } as Notification;
    }
  }
);

export const fetchNotificationPreferences = createAsyncThunk(
  'notifications/fetchPreferences',
  async (customerId?: string) => {
    try {
      const url = customerId 
        ? `/customers/${customerId}/notification-preferences`
        : '/notifications/preferences';
      const response = await api.get<ApiResponse<NotificationPreferences>>(url);
      return response.data.data;
    } catch (error: any) {
      // Fallback to mock data
      return {
        emailEnabled: true,
        pushEnabled: true,
        paymentNotifications: true,
        subscriptionNotifications: true,
        systemNotifications: false,
        securityNotifications: true,
      } as NotificationPreferences;
    }
  }
);

const notificationsSlice = createSlice({
  name: 'notifications',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    addNotification: (state, action: PayloadAction<Notification>) => {
      state.notifications.unshift(action.payload);
      if (!action.payload.isRead) {
        state.unreadCount += 1;
      }
    },
    updateUnreadCount: (state) => {
      state.unreadCount = state.notifications.filter(n => !n.isRead).length;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch notifications
      .addCase(fetchNotifications.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchNotifications.fulfilled, (state, action: PayloadAction<Notification[]>) => {
        state.loading = false;
        state.notifications = action.payload;
        state.unreadCount = action.payload.filter(n => !n.isRead).length;
      })
      .addCase(fetchNotifications.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch notifications';
      })
      // Mark as read
      .addCase(markAsRead.fulfilled, (state, action: PayloadAction<string>) => {
        const notification = state.notifications.find(n => n.id === action.payload);
        if (notification && !notification.isRead) {
          notification.isRead = true;
          state.unreadCount = Math.max(0, state.unreadCount - 1);
        }
      })
      // Mark all as read
      .addCase(markAllAsRead.fulfilled, (state) => {
        state.notifications.forEach(n => n.isRead = true);
        state.unreadCount = 0;
      })
      // Send notification
      .addCase(sendNotification.fulfilled, (state, action: PayloadAction<Notification>) => {
        state.notifications.unshift(action.payload);
        if (!action.payload.isRead) {
          state.unreadCount += 1;
        }
      })
      // Fetch preferences
      .addCase(fetchNotificationPreferences.fulfilled, (state, action: PayloadAction<NotificationPreferences>) => {
        state.preferences = action.payload;
      });
  },
});

export const { clearError, addNotification, updateUnreadCount } = notificationsSlice.actions;
export default notificationsSlice.reducer;